import {
    type ConditionExpression,
    type ConditionGroup,
    childGroups,
    type ParamChoice,
} from "./types";

/**
 * Rendering a condition as readable boolean logic.
 *
 * The API also returns a rendered ``summary`` string, but that is a flat block of text. To draw
 * the structure — nested groups, which operator joins what — the tree is needed, so the leaf
 * rendering is reproduced here. Keep the operator vocabulary below in step with
 * ConditionFormatter.expression_to_str; the two are read side by side by anyone comparing the
 * editor against the admin.
 */

/** How each stored operator reads. */
export const OPERATOR_SYMBOLS: Record<string, string> = {
    lt: "<",
    lte: "≤",
    gt: ">",
    gte: "≥",
    eq: "=",
    neq: "≠",
};

/** How a field reads, given the type that decides where its value comes from. */
export function fieldLabel(expression: ConditionExpression): string {
    if (expression.fieldType === "extra_data") {
        return `data_point.data['${expression.fieldName}']`;
    }
    if (expression.fieldType === "site_extra_data") {
        return `site.extra_data['${expression.fieldName}']`;
    }
    return expression.fieldName;
}

/** A single comparison, as one readable line. */
export function expressionText(expression: ConditionExpression): string {
    const field = fieldLabel(expression);

    if (expression.operator === "between") {
        return `${field} between [${expression.value}, ${expression.value2}]`;
    }

    const symbol = OPERATOR_SYMBOLS[expression.operator] ?? expression.operator;
    // Values are stored as strings; quoting the string-compared types keeps "10" distinct from
    // a numeric 10 when reading.
    const value =
        expression.fieldType === "numeric"
            ? expression.value
            : `"${expression.value}"`;
    return `${field} ${symbol} ${value}`;
}

/** One line of a rendered group: a comparison, a filter, or a nested group. */
export type LogicNode =
    | { kind: "expression"; text: string }
    | { kind: "filter"; text: string }
    | { kind: "group"; group: LogicGroup };

export interface LogicGroup {
    id: string;
    /** How this group's own lines are joined. */
    operator: "AND" | "OR";
    nodes: LogicNode[];
    /** True when nothing in the group narrows anything — it matches every event. */
    matchesEverything: boolean;
}

function labelFor(choices: ParamChoice[], value: string): string {
    return (
        choices.find((choice) => choice.value === value)?.label ??
        `${value} (unknown)`
    );
}

/**
 * Turn a stored group into renderable lines.
 *
 * Data providers and geo area are rendered as ordinary lines because that is how they behave:
 * the evaluator ANDs them with everything else in the group, whatever the group's own operator
 * is. Showing them as plain filters above an OR group would misrepresent that.
 */
export function toLogicGroup(
    group: ConditionGroup,
    providers: ParamChoice[],
    geoAreas: ParamChoice[],
): LogicGroup {
    const nodes: LogicNode[] = [];

    if (group.geoArea !== null) {
        nodes.push({
            kind: "filter",
            text: `within ${labelFor(geoAreas, String(group.geoArea))}`,
        });
    }

    if (group.dataProviders.length) {
        const names = group.dataProviders.map((id) =>
            labelFor(providers, String(id)),
        );
        nodes.push({
            kind: "filter",
            text:
                names.length === 1
                    ? `provider is ${names[0]}`
                    : `provider in [${names.join(", ")}]`,
        });
    }

    for (const expression of group.expressions) {
        nodes.push({ kind: "expression", text: expressionText(expression) });
    }

    for (const child of childGroups(group)) {
        nodes.push({
            kind: "group",
            group: toLogicGroup(child, providers, geoAreas),
        });
    }

    return {
        id: group.id,
        operator: group.logicalOperator,
        nodes,
        // An empty group evaluates true, which is the single most surprising thing the engine
        // does. It is called out wherever the tree is drawn.
        matchesEverything: nodes.length === 0,
    };
}

/**
 * The whole condition, as root groups.
 *
 * Root groups are OR'd — ConditionEvaluator.evaluate takes any() over them, and the group's own
 * logical_operator applies only within it.
 */
export function toLogicGroups(
    groups: ConditionGroup[],
    providers: ParamChoice[],
    geoAreas: ParamChoice[],
): LogicGroup[] {
    return groups.map((group) => toLogicGroup(group, providers, geoAreas));
}
